import * as THREE from 'three';

export const SKIN_TONES = ['#5a3a26', '#6f4a32', '#84573a', '#3f2a1c'];
export const SHIRT_COLORS = ['#f2b705', '#c8402f', '#2f6fb4', '#2f8a56', '#f4f1e8', '#7a3fa0', '#e07a28', '#1d2733'];
export const TROUSER_COLORS = ['#22262e', '#3b4a63', '#5a4634', '#2f3d2c'];

function box(w, h, d, color, x, y, z) {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), new THREE.MeshLambertMaterial({ color }));
  m.position.set(x, y, z);
  m.castShadow = true;
  return m;
}

// A limb that swings from its top end.
function limb(w, h, d, color, x, y, z) {
  const pivot = new THREE.Group();
  pivot.position.set(x, y, z);
  pivot.add(box(w, h, d, color, 0, -h / 2, 0));
  return pivot;
}

// Builds a simple person about 1.75 m tall, facing +z, with feet at the origin.
// look: { skin, shirt, trousers } as indexes into the palettes above.
export function makeCharacter(look = {}) {
  const skin = SKIN_TONES[(look.skin || 0) % SKIN_TONES.length];
  const shirt = SHIRT_COLORS[(look.shirt || 0) % SHIRT_COLORS.length];
  const trousers = TROUSER_COLORS[(look.trousers || 0) % TROUSER_COLORS.length];
  const group = new THREE.Group();
  const body = new THREE.Group();
  group.add(body);
  body.add(box(0.44, 0.58, 0.24, shirt, 0, 1.13, 0));
  body.add(box(0.4, 0.14, 0.23, trousers, 0, 0.8, 0));
  const head = box(0.25, 0.27, 0.25, skin, 0, 1.6, 0);
  body.add(head);
  body.add(box(0.27, 0.09, 0.27, '#17120e', 0, 1.75, -0.005));
  body.add(box(0.1, 0.08, 0.1, skin, 0, 1.44, 0));
  const armL = limb(0.11, 0.6, 0.12, shirt, 0.28, 1.4, 0);
  const armR = limb(0.11, 0.6, 0.12, shirt, -0.28, 1.4, 0);
  const legL = limb(0.16, 0.78, 0.17, trousers, 0.11, 0.78, 0);
  const legR = limb(0.16, 0.78, 0.17, trousers, -0.11, 0.78, 0);
  for (const arm of [armL, armR]) arm.add(box(0.1, 0.12, 0.11, skin, 0, -0.64, 0));
  for (const leg of [legL, legR]) leg.add(box(0.17, 0.08, 0.27, '#1a1a1a', 0, -0.76, 0.04));
  body.add(armL, armR, legL, legR);
  return { group, body, armL, armR, legL, legR, phase: 0 };
}

// Swings the limbs. speed is metres per second; seated folds the legs forward.
export function animateCharacter(c, speed, dt, seated = false) {
  if (seated) {
    c.legL.rotation.x = c.legR.rotation.x = -1.35;
    c.armL.rotation.x = c.armR.rotation.x = -0.9;
    c.body.position.y = -0.35;
    return;
  }
  c.body.position.y = 0;
  const moving = speed > 0.2;
  c.phase += dt * (moving ? 4 + speed * 1.6 : 0);
  const swing = moving ? Math.min(0.95, 0.35 + speed * 0.09) : 0;
  const s = Math.sin(c.phase) * swing;
  // settle back to rest smoothly when stopping
  const k = moving ? 1 : Math.max(0, 1 - dt * 10);
  c.legL.rotation.x = moving ? s : c.legL.rotation.x * k;
  c.legR.rotation.x = moving ? -s : c.legR.rotation.x * k;
  c.armL.rotation.x = moving ? -s * 0.8 : c.armL.rotation.x * k;
  c.armR.rotation.x = moving ? s * 0.8 : c.armR.rotation.x * k;
}
